/**
 * Reactions on nodes in the cloud (utils/nodeReactions.ts):
 *
 *   projects/{projectId}/reactions/{nodeId}__{userId}__{emoji index}    one person's reaction on one node
 *
 * Everyone a workspace is shared with gives and takes them back, as themselves, under the rules
 * (firestore.rules, "Reactions on nodes"); also those who may only view it, since a reaction is
 * feedback, as a comment is. Each reaction is its own record with a known id, so giving one and
 * taking it back are one write each, never a change to the workspace itself.
 */
import { getFirestore, collection, doc, onSnapshot, setDoc, deleteDoc } from 'firebase/firestore';
import { getFirebaseApp, isFirebaseActive, withCloudTimeout, describeCloudError } from './storage';
import { reactionKey, toReaction, type NodeReaction } from './nodeReactions';

export const reactionsAvailable = (): boolean => isFirebaseActive();

const db = () => {
    const app = getFirebaseApp();
    if (!app) throw new Error('Reactions need the cloud. Sign in to use them.');
    return getFirestore(app);
};

const reactions = (projectId: string) => collection(db(), 'projects', projectId, 'reactions');

/** A refusal from the rules most likely means the reactions section of firestore.rules is not deployed yet */
const describe = (e: unknown, fallback: string): string =>
    ((e as { code?: string })?.code === 'permission-denied'
        ? `${fallback}: the database does not allow reactions yet. The reactions rules in firestore.rules need to be deployed.`
        : describeCloudError(e, fallback));

type Stop = () => void;

/** Every reaction of a workspace, as they change */
export const watchReactions = (projectId: string, onReactions: (reactions: NodeReaction[]) => void, onError: (message: string) => void): Stop =>
    onSnapshot(
        reactions(projectId),
        (snapshot) => onReactions(snapshot.docs.map(d => toReaction(d.id, d.data() as Record<string, unknown>)).filter((r): r is NodeReaction => r !== null)),
        (error) => { console.error('[reactions] listener failed:', error); onError(describe(error, 'The reactions could not be loaded')); },
    );

/** One person's reaction on a node, given */
export const giveReaction = async (projectId: string, nodeId: string, emoji: string, userId: string): Promise<NodeReaction> => {
    const id = reactionKey(nodeId, userId, emoji);
    const reaction: NodeReaction = { id, nodeId, emoji, userId, createdAt: Date.now() };
    const { id: _id, ...data } = reaction;
    try {
        await withCloudTimeout(() => setDoc(doc(reactions(projectId), id), data));
    } catch (e) {
        console.error('[reactions] give failed:', e);
        throw new Error(describe(e, 'The reaction could not be given'));
    }
    return reaction;
};

/** One person's reaction on a node, taken back */
export const takeBackReaction = async (projectId: string, nodeId: string, emoji: string, userId: string): Promise<void> => {
    try {
        await withCloudTimeout(() => deleteDoc(doc(reactions(projectId), reactionKey(nodeId, userId, emoji))));
    } catch (e) {
        console.error('[reactions] take back failed:', e);
        throw new Error(describe(e, 'The reaction could not be taken back'));
    }
};
